import * as THREE from 'three';
import { UI_ICONS } from './icons';

export interface CropAnnotationDependencies {
  container: HTMLElement;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  /** Returns the camera currently shown in the viewport (Front/Top views swap cameras). */
  getCamera: () => THREE.Camera;
  moduleId: string;
  statusLog: { setStatus(message: string, status?: 'error' | 'info' | 'ok' | 'warning'): void };
}

/** Owns crop selection, canvas annotations, history, clipboard and PNG saving. */
export function createCropAnnotationController({
  container,
  renderer,
  scene,
  getCamera,
  moduleId,
  statusLog,
}: CropAnnotationDependencies) {
  let cropOverlay: HTMLDivElement | null = null;

  function endCropMode(): void {
    cropOverlay?.remove();
    cropOverlay = null;
  }

  /** Pull the chosen region out of the WebGL canvas as a PNG blob.
   *  The canvas has no preserveDrawingBuffer, so it must be re-rendered in the same tick as the
   *  readback - exactly what the ðŸ“¸ handler above does, and for the same reason. */
  async function cropToBlob(rectCss: { left: number; top: number; width: number; height: number }): Promise<Blob | null> {
    renderer.render(scene, getCamera());
    const canvas = renderer.domElement;
    const canvasRect = canvas.getBoundingClientRect();
    if (canvasRect.width < 1 || canvasRect.height < 1) return null;
    // CSS pixels -> canvas pixels. This also folds in devicePixelRatio, so the crop keeps full
    // resolution on a retina display instead of coming out half-size.
    const sx = canvas.width / canvasRect.width;
    const sy = canvas.height / canvasRect.height;
    const cx = Math.round(rectCss.left * sx);
    const cy = Math.round(rectCss.top * sy);
    const cw = Math.max(1, Math.round(rectCss.width * sx));
    const ch = Math.max(1, Math.round(rectCss.height * sy));

    const out = document.createElement('canvas');
    out.width = cw;
    out.height = ch;
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(canvas, cx, cy, cw, ch, 0, 0, cw, ch);
    return new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/png'));
  }

  function startCropMode(): void {
    endCropMode();
    const host = container;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    const hostRect = host.getBoundingClientRect();
    const canvas = renderer.domElement;
    const canvasRect = canvas.getBoundingClientRect();

    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:absolute;left:0;top:0;width:100%;height:100%;z-index:40;cursor:crosshair;' +
      'user-select:none;touch-action:none;';
    cropOverlay = overlay;

    const box = document.createElement('div');
    box.style.cssText =
      'position:absolute;display:none;border:2px dashed #3b82f6;' +
      'background:rgba(59,130,246,0.12);' +
      'box-shadow:0 0 0 9999px rgba(15,23,42,0.35);' +
      'pointer-events:none;z-index:41;';
    overlay.appendChild(box);

    let dragging = false;
    let startX = 0, startY = 0;
    let rectCss = { left: 0, top: 0, width: 0, height: 0 };

    const localPoint = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      return {
        x: e.clientX - r.left,
        y: e.clientY - r.top,
      };
    };

    // --- 2D Annotation Canvas Overlay ---
    const drawCanvas = document.createElement('canvas');
    drawCanvas.width = hostRect.width;
    drawCanvas.height = hostRect.height;
    drawCanvas.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:39;';
    overlay.appendChild(drawCanvas);
    const dctx = drawCanvas.getContext('2d')!;

    // --- Unified Control Panel (Docked at bottom center with sleek pill design) ---
    const controlDock = document.createElement('div');
    controlDock.style.cssText =
      'position:absolute;bottom:20px;left:50%;transform:translateX(-50%);z-index:45;' +
      'display:flex;flex-direction:column;align-items:center;gap:8px;' +
      'background:rgba(255,255,255,0.95);padding:8px 12px;border-radius:16px;' +
      'border:1px solid #cbd5e1;box-shadow:0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1);' +
      'backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);pointer-events:auto;' +
      'opacity:1;transition:opacity 120ms ease;';
    controlDock.addEventListener('pointerdown', (e) => e.stopPropagation());
    controlDock.addEventListener('mousedown', (e) => e.stopPropagation());

    // Top row inside Dock: Drawing Tools & Swatches
    const toolBar = document.createElement('div');
    toolBar.style.cssText = 'display:flex;align-items:center;gap:4px;';

    type AnnotationMode = 'select' | 'arrow' | 'pen' | 'text' | 'eraser';
    let currentTool: AnnotationMode = 'select';
    let currentColor = '#ef4444'; // default red arrow/pen
    let currentTextSize = 14;
    const toolBtns: Map<string, HTMLButtonElement> = new Map();

    const setTool = (tId: AnnotationMode) => {
      currentTool = tId;
      toolBtns.forEach((b, k) => {
        const active = k === currentTool;
        b.style.background = active ? '#eff6ff' : 'transparent';
        b.style.borderColor = active ? '#2563eb' : 'transparent';
        b.style.color = active ? '#2563eb' : '#475569';
      });
      overlay.style.cursor = tId === 'select' ? 'crosshair' : 'default';
      // Keep the dock visible while the user is choosing a tool. It is dimmed only after
      // the actual drawing gesture begins (pointerdown below).
      controlDock.style.opacity = '1';
    };

    interface ShapeItem {
      type: 'pen' | 'arrow' | 'text';
      color: string;
      points?: { x: number; y: number }[];
      from?: { x: number; y: number };
      to?: { x: number; y: number };
      text?: string;
      x?: number;
      y?: number;
      fontSize?: number;
    }

    const shapes: ShapeItem[] = [];
    const undoStack: ShapeItem[][] = [];
    const redoStack: ShapeItem[][] = [];

    const saveState = () => {
      undoStack.push(JSON.parse(JSON.stringify(shapes)));
      redoStack.length = 0;
    };

    const redrawAnnotations = () => {
      dctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
      for (const s of shapes) {
        dctx.strokeStyle = s.color;
        dctx.fillStyle = s.color;
        dctx.lineWidth = 2.5;
        dctx.lineCap = 'round';
        dctx.lineJoin = 'round';

        if (s.type === 'pen' && s.points && s.points.length > 0) {
          dctx.beginPath();
          const firstPoint = s.points[0];
          if (firstPoint) dctx.moveTo(firstPoint.x, firstPoint.y);
          for (let i = 1; i < s.points.length; i++) {
            const point = s.points[i];
            if (point) dctx.lineTo(point.x, point.y);
          }
          dctx.stroke();
        } else if (s.type === 'arrow' && s.from && s.to) {
          const { x: x1, y: y1 } = s.from;
          const { x: x2, y: y2 } = s.to;
          const headlen = 24; // doubled from 12 (2x larger arrow head)
          const angle = Math.atan2(y2 - y1, x2 - x1);
          dctx.lineWidth = 5.0; // doubled from 2.5
          dctx.beginPath();
          dctx.moveTo(x1, y1);
          dctx.lineTo(x2, y2);
          dctx.stroke();
          // Arrow head
          dctx.beginPath();
          dctx.moveTo(x2, y2);
          dctx.lineTo(x2 - headlen * Math.cos(angle - Math.PI / 6), y2 - headlen * Math.sin(angle - Math.PI / 6));
          dctx.lineTo(x2 - headlen * Math.cos(angle + Math.PI / 6), y2 - headlen * Math.sin(angle + Math.PI / 6));
          dctx.closePath();
          dctx.fill();
        } else if (s.type === 'text' && s.text && s.x !== undefined && s.y !== undefined) {
          dctx.font = `600 ${s.fontSize ?? 14}px var(--mono-font, sans-serif)`;
          dctx.fillText(s.text, s.x, s.y);
        }
      }
    };

    let isDrawingShape = false;
    let shapeStart = { x: 0, y: 0 };
    let currentPenPoints: { x: number; y: number }[] = [];

    const arrowIntersectsDock = (end: { x: number; y: number }): boolean => {
      const dock = controlDock.getBoundingClientRect();
      const hostRectNow = host.getBoundingClientRect();
      const x0 = hostRectNow.left + shapeStart.x;
      const y0 = hostRectNow.top + shapeStart.y;
      const dx = hostRectNow.left + end.x - x0;
      const dy = hostRectNow.top + end.y - y0;
      let t0 = 0;
      let t1 = 1;
      const clip = (p: number, q: number): boolean => {
        if (Math.abs(p) < 1e-8) return q >= 0;
        const t = q / p;
        if (p < 0) {
          if (t > t1) return false;
          t0 = Math.max(t0, t);
        } else {
          if (t < t0) return false;
          t1 = Math.min(t1, t);
        }
        return true;
      };
      return clip(-dx, x0 - dock.left) && clip(dx, dock.right - x0) &&
        clip(-dy, y0 - dock.top) && clip(dy, dock.bottom - y0) && t0 <= t1;
    };

    const drawBox = () => {
      box.style.display = rectCss.width > 1 && rectCss.height > 1 ? 'block' : 'none';
      box.style.left = `${rectCss.left}px`;
      box.style.top = `${rectCss.top}px`;
      box.style.width = `${rectCss.width}px`;
      box.style.height = `${rectCss.height}px`;
    };

    overlay.addEventListener('pointerdown', (e) => {
      const p = localPoint(e);
      if (currentTool === 'select') {
        dragging = true;
        startX = p.x;
        startY = p.y;
        rectCss = { left: p.x, top: p.y, width: 0, height: 0 };
        drawBox();
        overlay.setPointerCapture(e.pointerId);
        controlDock.style.display = 'none';
      } else if (currentTool === 'arrow') {
        controlDock.style.opacity = '0.25';
        saveState();
        isDrawingShape = true;
        shapeStart = p;
        overlay.setPointerCapture(e.pointerId);
      } else if (currentTool === 'pen') {
        controlDock.style.opacity = '0.25';
        saveState();
        isDrawingShape = true;
        currentPenPoints = [p];
        overlay.setPointerCapture(e.pointerId);
      } else if (currentTool === 'eraser') {
        controlDock.style.opacity = '0.25';
        saveState();
        // Remove shape nearest to click point
        const idx = shapes.findIndex((s) => {
          if (s.from && s.to) {
            return Math.abs(p.x - s.to.x) < 20 && Math.abs(p.y - s.to.y) < 20;
          }
          if (s.points) {
            return s.points.some((pt) => Math.hypot(pt.x - p.x, pt.y - p.y) < 15);
          }
          if (s.x !== undefined && s.y !== undefined) {
            return Math.hypot(s.x - p.x, s.y - p.y) < 20;
          }
          return false;
        });
        if (idx !== -1) {
          shapes.splice(idx, 1);
          redrawAnnotations();
        }
        controlDock.style.opacity = '1';
      } else if (currentTool === 'text') {
        controlDock.style.opacity = '0.25';
        const val = prompt('Enter annotation text:');
        if (val && val.trim()) {
          saveState();
          shapes.push({ type: 'text', color: currentColor, text: val.trim(), x: p.x, y: p.y, fontSize: currentTextSize });
          redrawAnnotations();
        }
        controlDock.style.opacity = '1';
      }
    });

    overlay.addEventListener('pointermove', (e) => {
      const p = localPoint(e);
      if (dragging && currentTool === 'select') {
        rectCss = {
          left: Math.min(startX, p.x),
          top: Math.min(startY, p.y),
          width: Math.abs(p.x - startX),
          height: Math.abs(p.y - startY),
        };
        drawBox();
      } else if (isDrawingShape) {
        if (currentTool === 'pen') {
          currentPenPoints.push(p);
          // temporary live preview
          redrawAnnotations();
          dctx.strokeStyle = currentColor;
          dctx.lineWidth = 2.5;
          dctx.beginPath();
          const firstPoint = currentPenPoints[0];
          if (firstPoint) dctx.moveTo(firstPoint.x, firstPoint.y);
          for (let i = 1; i < currentPenPoints.length; i++) {
            const point = currentPenPoints[i];
            if (point) dctx.lineTo(point.x, point.y);
          }
          dctx.stroke();
        } else if (currentTool === 'arrow') {
          redrawAnnotations();
          dctx.strokeStyle = currentColor;
          dctx.fillStyle = currentColor;
          dctx.lineWidth = 5.0; // doubled from 2.5
          const { x: x1, y: y1 } = shapeStart;
          const { x: x2, y: y2 } = p;
          const headlen = 24; // doubled from 12 (2x larger arrow head)
          const angle = Math.atan2(y2 - y1, x2 - x1);
          dctx.beginPath();
          dctx.moveTo(x1, y1);
          dctx.lineTo(x2, y2);
          dctx.stroke();
          dctx.beginPath();
          dctx.moveTo(x2, y2);
          dctx.lineTo(x2 - headlen * Math.cos(angle - Math.PI / 6), y2 - headlen * Math.sin(angle - Math.PI / 6));
          dctx.lineTo(x2 - headlen * Math.cos(angle + Math.PI / 6), y2 - headlen * Math.sin(angle + Math.PI / 6));
          dctx.closePath();
          dctx.fill();
        }
      }
    });

    overlay.addEventListener('pointerup', (e) => {
      if (dragging) {
        dragging = false;
        try { overlay.releasePointerCapture(e.pointerId); } catch { /* released */ }
        if (rectCss.width < 8 || rectCss.height < 8) {
          box.style.display = 'none';
        } else {
          // After crop selection finishes -> automatically select arrow tool by default
          setTool('arrow');
        }
        controlDock.style.display = 'flex';
      } else if (isDrawingShape) {
        isDrawingShape = false;
        controlDock.style.opacity = '1';
        try { overlay.releasePointerCapture(e.pointerId); } catch { /* released */ }
        const p = localPoint(e);
        if (currentTool === 'pen' && currentPenPoints.length > 1) {
          shapes.push({ type: 'pen', color: currentColor, points: [...currentPenPoints] });
        } else if (currentTool === 'arrow') {
          shapes.push({ type: 'arrow', color: currentColor, from: { ...shapeStart }, to: { ...p } });
        }
        redrawAnnotations();
      }
    });
    overlay.addEventListener('pointercancel', () => {
      dragging = false;
      isDrawingShape = false;
      controlDock.style.opacity = '1';
      controlDock.style.display = 'flex';
    });

    const tools: Array<{ id: AnnotationMode; icon: string; title: string }> = [
      { id: 'select', icon: UI_ICONS.crop, title: 'Crop Select Region' },
      { id: 'arrow', icon: UI_ICONS.arrow, title: 'Draw Arrow' },
      { id: 'pen', icon: UI_ICONS.pen, title: 'Freehand Pen' },
      { id: 'text', icon: UI_ICONS.text, title: 'Add Text' },
      { id: 'eraser', icon: UI_ICONS.eraser, title: 'Eraser' },
    ];

    tools.forEach((t) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.title = t.title;
      btn.innerHTML = t.icon;
      btn.style.cssText = `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        min-width: 30px;
        min-height: 30px;
        padding: 0;
        margin: 0;
        box-sizing: border-box;
        border-radius: 8px;
        border: 1px solid ${t.id === 'select' ? '#2563eb' : 'transparent'};
        background: ${t.id === 'select' ? '#eff6ff' : 'transparent'};
        color: ${t.id === 'select' ? '#2563eb' : '#475569'};
        cursor: pointer;
        transition: all 0.15s ease;
        flex-shrink: 0;
      `;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        setTool(t.id);
      });
      toolBtns.set(t.id, btn);
      toolBar.appendChild(btn);
    });

    // Divider line
    const divider1 = document.createElement('div');
    divider1.style.cssText = 'width:1px;height:16px;background:#cbd5e1;margin:0 4px;flex-shrink:0;';
    toolBar.appendChild(divider1);

    // Color Swatch Picker wrapper (square 30x30 with circular preview, perfectly centered)
    const colorWrapper = document.createElement('button');
    colorWrapper.type = 'button';
    colorWrapper.title = 'Choose Annotation Color';
    colorWrapper.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      min-width: 30px;
      min-height: 30px;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
      border-radius: 8px;
      border: 1px solid transparent;
      background: transparent;
      cursor: pointer;
      flex-shrink: 0;
    `;

    const colorCircle = document.createElement('span');
    colorCircle.style.cssText = `width:18px;height:18px;border-radius:50%;background:${currentColor};box-shadow:0 0 0 1.5px #e2e8f0, inset 0 0 0 1px rgba(0,0,0,0.15);display:block;margin:auto;flex-shrink:0;`;
    colorWrapper.appendChild(colorCircle);

    const colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.value = currentColor;
    colorInput.style.cssText = 'position:absolute;opacity:0;width:0;height:0;pointer-events:none;';
    colorInput.addEventListener('input', (e) => {
      currentColor = (e.target as HTMLInputElement).value;
      colorCircle.style.background = currentColor;
    });
    colorWrapper.appendChild(colorInput);
    colorWrapper.addEventListener('click', () => colorInput.click());
    toolBar.appendChild(colorWrapper);

    const textSizeGroup = document.createElement('div');
    textSizeGroup.style.cssText = 'display:inline-flex;align-items:center;gap:2px;margin-left:2px;';
    const textSizeValue = document.createElement('span');
    textSizeValue.style.cssText = 'min-width:25px;text-align:center;color:#475569;font:600 10px var(--mono-font,monospace);';
    const updateTextSize = (delta: number) => {
      currentTextSize = Math.min(48, Math.max(8, currentTextSize + delta));
      textSizeValue.textContent = `${currentTextSize}px`;
    };
    const mkTextSizeButton = (label: string, title: string, delta: number) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.title = title;
      button.style.cssText = 'width:26px;height:30px;padding:0;border:1px solid transparent;border-radius:8px;background:transparent;color:#475569;font:700 12px system-ui,sans-serif;cursor:pointer;';
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        updateTextSize(delta);
      });
      return button;
    };
    textSizeValue.textContent = `${currentTextSize}px`;
    textSizeGroup.append(
      mkTextSizeButton('A−', 'Decrease text size', -2),
      textSizeValue,
      mkTextSizeButton('A+', 'Increase text size', 2),
    );
    toolBar.appendChild(textSizeGroup);

    // Divider line
    const divider2 = document.createElement('div');
    divider2.style.cssText = 'width:1px;height:16px;background:#cbd5e1;margin:0 4px;flex-shrink:0;';
    toolBar.appendChild(divider2);

    // Undo / Redo Buttons
    const undoBtn = document.createElement('button');
    undoBtn.type = 'button';
    undoBtn.title = 'Undo Annotation';
    undoBtn.innerHTML = UI_ICONS.undo;
    undoBtn.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      min-width: 30px;
      min-height: 30px;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
      border-radius: 8px;
      border: 1px solid transparent;
      background: transparent;
      color: #475569;
      cursor: pointer;
      flex-shrink: 0;
    `;
    undoBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (undoStack.length > 0) {
        redoStack.push(JSON.parse(JSON.stringify(shapes)));
        const prev = undoStack.pop()!;
        shapes.length = 0;
        shapes.push(...prev);
        redrawAnnotations();
      }
    });
    toolBar.appendChild(undoBtn);

    const redoBtn = document.createElement('button');
    redoBtn.type = 'button';
    redoBtn.title = 'Redo Annotation';
    redoBtn.innerHTML = UI_ICONS.redo;
    redoBtn.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      min-width: 30px;
      min-height: 30px;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
      border-radius: 8px;
      border: 1px solid transparent;
      background: transparent;
      color: #475569;
      cursor: pointer;
      flex-shrink: 0;
    `;
    redoBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (redoStack.length > 0) {
        undoStack.push(JSON.parse(JSON.stringify(shapes)));
        const next = redoStack.pop()!;
        shapes.length = 0;
        shapes.push(...next);
        redrawAnnotations();
      }
    });
    toolBar.appendChild(redoBtn);

    controlDock.appendChild(toolBar);

    // Horizontal Separator inside dock
    const rowSeparator = document.createElement('div');
    rowSeparator.style.cssText = 'width:100%;height:1px;background:#e2e8f0;margin:2px 0;';
    controlDock.appendChild(rowSeparator);

    // Bottom row inside Dock: Action Buttons (Copy / Save / Cancel)
    const buttonsRow = document.createElement('div');
    buttonsRow.style.cssText = 'display:flex;align-items:center;gap:8px;width:100%;justify-content:center;';

    const mkBtn = (label: string, primary: boolean, fn: () => void) => {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText =
        'font:600 11px system-ui,sans-serif;padding:6px 16px;border-radius:8px;cursor:pointer;transition:all 0.15s ease;' +
        (primary
          ? 'background:#2563eb;color:#fff;border:1px solid #2563eb;box-shadow:0 2px 4px rgba(37,99,235,0.2);'
          : 'background:#f8fafc;color:#334155;border:1px solid #cbd5e1;');
      b.addEventListener('click', fn);
      return b;
    };

    /** Canvas-relative CSS rect for the current selection, or full canvas if no region drawn */
    const selectionInCanvasCss = () => {
      if (rectCss.width > 5 && rectCss.height > 5) {
        return {
          left: rectCss.left + hostRect.left - canvasRect.left,
          top: rectCss.top + hostRect.top - canvasRect.top,
          width: rectCss.width,
          height: rectCss.height,
        };
      }
      return {
        left: 0,
        top: 0,
        width: canvasRect.width,
        height: canvasRect.height,
      };
    };

    /** Render WebGL scene AND composite any drawn 2D annotations into the output blob. */
    const renderCompositedBlob = async (rectCss: { left: number; top: number; width: number; height: number }): Promise<Blob | null> => {
      renderer.render(scene, getCamera());
      const canvas = renderer.domElement;
      const canvasRect = canvas.getBoundingClientRect();
      if (canvasRect.width < 1 || canvasRect.height < 1) return null;

      const sx = canvas.width / canvasRect.width;
      const sy = canvas.height / canvasRect.height;
      const cx = Math.max(0, Math.round(rectCss.left * sx));
      const cy = Math.max(0, Math.round(rectCss.top * sy));
      const cw = Math.min(canvas.width - cx, Math.max(1, Math.round(rectCss.width * sx)));
      const ch = Math.min(canvas.height - cy, Math.max(1, Math.round(rectCss.height * sy)));

      const out = document.createElement('canvas');
      out.width = cw;
      out.height = ch;
      const ctx = out.getContext('2d');
      if (!ctx) return null;

      // 1. Draw 3D Viewport base image
      ctx.drawImage(canvas, cx, cy, cw, ch, 0, 0, cw, ch);
      // 2. Composite 2D annotations on top. The annotation canvas is stored in CSS pixels,
      // while the WebGL canvas can be DPR-scaled (often 2x). Convert the crop back to the
      // annotation canvas coordinate space so marks stay aligned and are not clipped.
      const cropLeftCss = cx / sx;
      const cropTopCss = cy / sy;
      const cropWidthCss = cw / sx;
      const cropHeightCss = ch / sy;
      const annotationScaleX = drawCanvas.width / hostRect.width;
      const annotationScaleY = drawCanvas.height / hostRect.height;
      const annotationLeft = (cropLeftCss + canvasRect.left - hostRect.left) * annotationScaleX;
      const annotationTop = (cropTopCss + canvasRect.top - hostRect.top) * annotationScaleY;
      ctx.drawImage(
        drawCanvas,
        annotationLeft,
        annotationTop,
        cropWidthCss * annotationScaleX,
        cropHeightCss * annotationScaleY,
        0,
        0,
        cw,
        ch,
      );

      return new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/png'));
    };

    const doCopy = async () => {
      const blob = await renderCompositedBlob(selectionInCanvasCss());
      if (!blob) { statusLog.setStatus('Crop failed: could not read the canvas', 'error'); return; }
      try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        statusLog.setStatus('âœ“ Cropped annotated image copied to clipboard', 'ok');
        endCropMode();
      } catch (err: any) {
        statusLog.setStatus(`Copy failed (${err?.message ?? err}) â€” use Save instead`, 'error');
      }
    };

    const copyBtn = mkBtn('Copy', true, doCopy);
    buttonsRow.appendChild(copyBtn);

    buttonsRow.appendChild(mkBtn('Save', false, async () => {
      const blob = await renderCompositedBlob(selectionInCanvasCss());
      if (!blob) { statusLog.setStatus('Crop failed: could not read the canvas', 'error'); return; }
      const defaultName = `${moduleId}_crop_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;

      // 1. Modern File System Access API: Opens "Save As" file browser dialog
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: defaultName,
            types: [
              {
                description: 'PNG Image (*.png)',
                accept: { 'image/png': ['.png'] },
              },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          statusLog.setStatus('âœ“ Image saved successfully', 'ok');
          endCropMode();
          return;
        } catch (err: any) {
          // If user aborted / cancelled the Save As dialog, do not trigger fallback download
          if (err?.name === 'AbortError') {
            return;
          }
          console.warn('[Crop Save] showSaveFilePicker failed, using fallback download:', err);
        }
      }

      // 2. Fallback for browsers without File System Access API
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = defaultName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      statusLog.setStatus('âœ“ Cropped annotated image saved', 'ok');
      endCropMode();
    }));

    buttonsRow.appendChild(mkBtn('Cancel', false, () => endCropMode()));
    controlDock.appendChild(buttonsRow);

    // Small shortcut tip right below the action buttons
    const keyTip = document.createElement('div');
    keyTip.innerHTML = 'Press <kbd style="background:#e2e8f0;color:#0f172a;padding:1px 5px;border-radius:4px;font-family:monospace;font-size:10px;">Enter â†µ</kbd> to copy selection';
    keyTip.style.cssText = 'font-size:10px;color:#64748b;text-align:center;width:100%;pointer-events:none;';
    controlDock.appendChild(keyTip);

    overlay.appendChild(controlDock);
    host.appendChild(overlay);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        endCropMode();
        window.removeEventListener('keydown', onKey);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        doCopy();
        window.removeEventListener('keydown', onKey);
      }
    };
    window.addEventListener('keydown', onKey);
    statusLog.setStatus('â¬š Crop: drag a region, then press Enter or click Copy/Save', 'info');
  }
  return { startCropMode, endCropMode };
}
